import { useState } from 'react';
import { Check, Truck, X } from 'lucide-react';

import { Chip, ChipLabel, ToggleChip, type ChipTone } from '@/components/chip';

const READ_ONLY_TONES: readonly ChipTone[] = [
  'neutral',
  'info',
  'success',
  'destructive',
  'warning',
  'muted',
  'outline',
];

function ReadOnlyChipsRow() {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {READ_ONLY_TONES.map((tone) => (
        <Chip key={tone} tone={tone}>
          {tone}
        </Chip>
      ))}
    </div>
  );
}

function SizesRow() {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Chip size="sm" tone="info">
        sm
      </Chip>
      <Chip size="default" tone="info">
        default
      </Chip>
      <Chip size="lg" tone="info">
        lg
      </Chip>
    </div>
  );
}

function LabelValueRow() {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Chip tone="info">
        <ChipLabel>Transit days</ChipLabel>
        14
      </Chip>
      <Chip tone="success">
        <ChipLabel>BL</ChipLabel>
        <Check aria-hidden />
      </Chip>
      <Chip tone="destructive">
        <ChipLabel>Suppl. inv. paid</ChipLabel>
        <X aria-hidden />
      </Chip>
      <Chip tone="muted">
        <ChipLabel>Buyer inv. paid</ChipLabel>
        None
      </Chip>
      <Chip tone="neutral">
        <Truck aria-hidden />
        <ChipLabel>In transit</ChipLabel>
      </Chip>
    </div>
  );
}

function ToggleChipsRow() {
  const [selected, setSelected] = useState<Set<string>>(new Set(['METYCLE', 'CALF']));
  const partners = ['METYCLE', 'CALF', 'SCCF', 'Rivonia', 'YONGGANG'];
  return (
    <div className="flex flex-wrap items-center gap-2">
      {partners.map((p) => (
        <ToggleChip
          key={p}
          selected={selected.has(p)}
          onClick={() => {
            setSelected((prev) => {
              const next = new Set(prev);
              if (next.has(p)) next.delete(p);
              else next.add(p);
              return next;
            });
          }}
        >
          {p}
        </ToggleChip>
      ))}
    </div>
  );
}

function ToggleTonesRow() {
  const [selected, setSelected] = useState<Record<string, boolean>>({
    success: true,
    info: true,
    warning: false,
    destructive: false,
  });
  const toggle = (key: string) => {
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {(['success', 'info', 'warning', 'destructive'] as ChipTone[]).map((tone) => (
        <ToggleChip
          key={tone}
          selected={selected[tone] === true}
          selectedTone={tone}
          onClick={() => {
            toggle(tone);
          }}
        >
          selected = {tone}
        </ToggleChip>
      ))}
    </div>
  );
}

interface RowProps {
  label: string;
  description: string;
  children: React.ReactNode;
}

function Row({ label, description, children }: Readonly<RowProps>) {
  return (
    <div className="space-y-2">
      <div>
        <p className="font-mono text-xs font-semibold">{label}</p>
        <p className="text-muted-foreground text-xs">{description}</p>
      </div>
      {children}
    </div>
  );
}

export function ChipsSection() {
  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Chips</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Compact pill primitives for status, metadata, and click-to-toggle selections. Two shapes
          consume the same tone palette: <code className="font-mono">{`<Chip>`}</code> for
          read-only, <code className="font-mono">{`<ToggleChip>`}</code> for selectable. Compose
          with <code className="font-mono">{`<ChipLabel>`}</code> for the uppercase mini-prefix.
        </p>
      </div>
      <Row label="Tones" description="Seven role-based tones for read-only chips.">
        <ReadOnlyChipsRow />
      </Row>
      <Row label="Sizes" description="sm · default · lg.">
        <SizesRow />
      </Row>
      <Row label="Label + value / icon" description="Compose with <ChipLabel> + text or icon.">
        <LabelValueRow />
      </Row>
      <Row
        label="<ToggleChip>"
        description="Click to toggle; aria-pressed reflects state. Defaults: success on, outline off."
      >
        <ToggleChipsRow />
      </Row>
      <Row label="Selected tones" description="selectedTone picks the on-state color.">
        <ToggleTonesRow />
      </Row>
    </section>
  );
}
